import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Header } from "./Header";
import { LinkTable } from "./LinkTable";
import { LinkForm } from "./LinkForm";
import { LinkStats } from "./LinkStats";
import { WhitelistManager } from "./WhitelistManager";
import { useLinks } from "../hooks/useLinks";
import type { User } from "firebase/auth";
import type { VanityLink, VanityLinkWrite } from "../../api/_lib/types";

interface Props {
  user: User;
  onSignOut: () => void;
}

export function Dashboard({ user, onSignOut }: Props) {
  const { links, loading, create, update, remove, load } = useLinks();
  const [formOpen, setFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<VanityLink | null>(null);
  const [statsLink, setStatsLink] = useState<VanityLink | null>(null);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data: VanityLinkWrite) => {
    if (editingLink) {
      const { slug: _, ...rest } = data;
      await update(editingLink.slug, rest);
      toast.success("Link updated");
    } else {
      await create(data);
      toast.success("Link created");
    }
  };

  const handleEdit = (link: VanityLink) => {
    setEditingLink(link);
    setFormOpen(true);
  };

  const handleDelete = async (slug: string) => {
    if (!confirm(`Delete /${slug}? This cannot be undone.`)) return;
    await remove(slug);
    toast.success("Link deleted");
  };

  const handleCreate = () => {
    setEditingLink(null);
    setFormOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onSignOut={onSignOut} />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Tabs defaultValue="links">
          <TabsList className="mb-8">
            <TabsTrigger value="links">Links</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="links">
            <LinkTable
              links={links}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onStats={setStatsLink}
              onCreate={handleCreate}
            />
          </TabsContent>

          <TabsContent value="settings">
            <WhitelistManager currentUser={user} />
          </TabsContent>
        </Tabs>
      </main>

      <LinkForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingLink(null); }}
        onSave={handleSave}
        initial={editingLink}
      />

      <LinkStats
        link={statsLink}
        onClose={() => setStatsLink(null)}
      />
    </div>
  );
}
